import React, { useMemo, useState } from "react";
import { Button, Modal } from "antd";
import { PlusOutlined, DownloadOutlined } from "@ant-design/icons";

import Comments from "components/comments";
import Events from "components/events";
import Icons from "components/icons";
import Stands from "components/stands";
import { Localize } from "components/service";
import { useBreadCrumbs } from "components/use-hooks";
import "components/comments/comments.scss";

const eventBasePath = Events.Const.basePath;
const standBasePath = Stands.Const.basePath;

const mapAttachment = (attachment) => ({
    id: +attachment?.id,
    name: attachment?.original_name || attachment?.server_name || attachment?.path || "",
});

const mapComment = (comment) => ({
    id: +comment?.id,
    text: comment?.content || "",
    author: `${ comment?.relUser?.name || "" } ${ comment?.relUser?.surname || "" }`.trim(),
    createdAt: comment?.created_at,
    attachments: (comment?.relAttachments || []).map(mapAttachment),
});

const parseErrorMessage = (error) => {
    if (error?.graphQLErrors?.length) {
        return error.graphQLErrors[ 0 ]?.message || "Request error";
    }
    return error?.message || "Request error";
};

const StandCommentsSubPage = ({ event, stand }) => {
    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Exhibitions" }).props
                .children,
            path: `${eventBasePath}`,
        },
        {
            label: event?.title,
            path: `${eventBasePath}/${event?.id}`,
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_Stands" }).props.children,
            path: `${eventBasePath}/${event?.id}${standBasePath}`,
        },
        {
            label: stand?.exhibitor,
            path: `${eventBasePath}/${event?.id}${standBasePath}/${stand?.id}`,
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_Comments" }).props
                .children,
            path: `${eventBasePath}/${event?.id}${standBasePath}/${stand?.id}/comments`,
        },
    ];

    useBreadCrumbs(breadcrumbs);

    const standID = +stand?.id;

    const [ isModalOpen, setIsModalOpen ] = useState(false);
    const [ modalMode, setModalMode ] = useState("add");
    const [ activeComment, setActiveComment ] = useState({
        id: null,
        text: "",
        attachments: [],
    });
    const [ modalError, setModalError ] = useState("");

    const {
        dataStandComments,
        refetchStandComments,
    } = Stands.Hooks.queryComments({
        standID,
        first: 100,
    });

    const comments = useMemo(
        () => (dataStandComments?.data || []).map(mapComment),
        [ dataStandComments?.data ],
    );

    const {
        _loadStandComment,
        loadingLazyQueryStandComment,
    } = Stands.Hooks.lazyQueryComment();

    const {
        _setStandCommentCreate,
    } = Stands.Hooks.commentCreate({
        showSuccessNotification: false,
        showErrorNotification: false,
        onError: (error) => {
            setModalError(parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentUpdate,
        loadingMutationStandCommentUpdate,
    } = Stands.Hooks.commentUpdate({
        showErrorNotification: false,
        onCompleted: () => {
            setIsModalOpen(false);
            setModalError("");
            setActiveComment({
                id: null,
                text: "",
                attachments: [],
            });
            refetchStandComments();
        },
        onError: (error) => {
            setModalError(parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentDelete,
        loadingMutationStandCommentDelete,
    } = Stands.Hooks.commentDelete({
        showSuccessNotification: false,
        showErrorNotification: false,
        onCompleted: () => {
            refetchStandComments();
        },
        onError: (error) => {
            setModalError(parseErrorMessage(error));
        },
    });

    const {
        _setStandAttachmentCreate,
        loadingMutationStandAttachmentCreate,
    } = Stands.Hooks.attachmentCreate({
        showSuccessNotification: false,
        showErrorNotification: false,
        onCompleted: (payload) => {
            const nextAttachment = payload?.attachment;
            if (!nextAttachment?.id) {
                return;
            }
            setActiveComment((prev) => ({
                ...prev,
                attachments: [ ...prev.attachments, mapAttachment(nextAttachment) ],
            }));
        },
        onError: (error) => {
            setModalError(parseErrorMessage(error));
        },
    });

    const {
        _setStandAttachmentDelete,
        loadingMutationStandAttachmentDelete,
    } = Stands.Hooks.attachmentDelete({
        showSuccessNotification: false,
        showErrorNotification: false,
        onError: (error) => {
            setModalError(parseErrorMessage(error));
        },
    });

    const syncCommentForModal = async (commentID) => {
        if (!commentID) {
            return;
        }

        const { data } = await _loadStandComment({
            variables: {
                id: +commentID,
            }
        });
        const mapped = mapComment(data?.comment);
        setActiveComment({
            id: mapped.id,
            text: mapped.text,
            attachments: mapped.attachments,
        });
    };

    const openAddModal = async () => {
        setModalError("");
        setModalMode("add");
        setIsModalOpen(true);
        setActiveComment({
            id: null,
            text: "",
            attachments: [],
        });

        const { data } = await _setStandCommentCreate({
            variables: {
                input: {
                    model: "Stand",
                    model_id: standID,
                    content: "",
                }
            }
        }).catch(() => ({ data: null }));

        const draftID = data?.commentCreate?.comment?.id;
        if (!draftID) {
            return;
        }

        await syncCommentForModal(draftID).catch(() => {});
    };

    const openEditModal = async (commentID) => {
        setModalError("");
        setModalMode("edit");
        setIsModalOpen(true);
        await syncCommentForModal(commentID).catch((error) => {
            setModalError(parseErrorMessage(error));
        });
    };

    const handleAddAttachment = (file) => {
        if (!activeComment?.id || activeComment?.attachments?.length >= 3) {
            return;
        }
        setModalError("");
        _setStandAttachmentCreate({
            variables: {
                file,
                model: "Comment",
                model_id: activeComment.id,
            }
        });
    };

    const handleRemoveAttachment = async (attachmentID) => {
        if (!attachmentID) {
            return;
        }
        await _setStandAttachmentDelete({
            variables: {
                id: +attachmentID,
            }
        });
        setActiveComment((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((item) => item.id !== +attachmentID),
        }));
    };

    const closeModalState = () => {
        setIsModalOpen(false);
        setModalError("");
        setActiveComment({
            id: null,
            text: "",
            attachments: [],
        });
    };

    const handleCancelModal = async () => {
        if (modalMode === "add" && activeComment?.id) {
            const attachmentIds = activeComment.attachments.map((item) => item.id).filter(Boolean);
            if (attachmentIds.length) {
                await Promise.all(
                    attachmentIds.map((attachmentID) =>
                        _setStandAttachmentDelete({
                            variables: {
                                id: +attachmentID,
                            }
                        }),
                    ),
                ).catch(() => {});
            }
            await _setStandCommentDelete({
                variables: {
                    id: +activeComment.id,
                }
            }).catch(() => {});
        }
        closeModalState();
    };

    const handleSubmitModal = ({ text }) => {
        if (!activeComment?.id) {
            return;
        }
        setModalError("");
        _setStandCommentUpdate({
            variables: {
                input: {
                    id: +activeComment.id,
                    model: "Stand",
                    model_id: standID,
                    content: text || "",
                }
            }
        });
    };

    const handleDeleteComment = (commentID) => {
        _setStandCommentDelete({
            variables: {
                id: +commentID,
            }
        });
    };

    return (
        <div className="comments-sub-page">

            {!!comments.length && (
                <div className="table-action-bar comments-action-bar">
                    <div className="col"></div>
                    <div className="col">
                        <Button className="ant-btn-secondary comments-export-btn">
                            <DownloadOutlined />{" "}
                            <Localize>COMMENTS.Button_Text_Export</Localize>
                        </Button>

                        <Button type="primary" onClick={ openAddModal }>
                            <PlusOutlined />{" "}
                            <Localize>COMMENTS.Button_Text_AddUpper</Localize>
                        </Button>
                    </div>
                </div>
            )}

            <div className="row-grid column-gap-60 col-lg-2">
                <div className="col-left">
                    <div className="comments-content">
                        {!comments.length && (
                            <div className="comments-empty-state">
                                <strong>
                                    <Localize>COMMENTS.Text_NoComments</Localize>
                                </strong>
                                <Icons.CommentsIllustration className="comments-empty-state-icon" />
                                <Button type="primary" onClick={ openAddModal }>
                                    <PlusOutlined />{" "}
                                    <Localize>COMMENTS.Button_Text_AddUpper</Localize>
                                </Button>
                            </div>
                        )}

                        {!!comments.length && (
                            <div className="comments-list">
                                {comments.map((comment) => (
                                    <div className="comment-card-wrap" key={comment.id}>
                                        <div className="comment-card-date">
                                            {Comments.Helpers.formatDate(comment.createdAt)}
                                        </div>

                                        <div className="comment-card">
                                            <div className="comment-card-header">
                                                <strong className="comment-card-author">
                                                    {comment.author}
                                                </strong>
                                                <Comments.Fields.Action
                                                    comment={comment}
                                                    onEdit={ openEditModal }
                                                    onDelete={ handleDeleteComment }
                                                />
                                            </div>

                                            <p className="comment-card-text">{comment.text}</p>

                                            {!!comment.attachments?.length && (
                                                <div className="comment-card-attachments">
                                                    {comment.attachments.map((attachment, index) => (
                                                        <span
                                                            className="comment-attachment-pill"
                                                            key={`${comment.id}-${index}`}
                                                            title={attachment.name}
                                                        >
                                                            <span className="attachment-icon">
                                                                <Icons.Attachment />
                                                            </span>
                                                            <span>
                                                                {Comments.Helpers.formatAttachmentName(attachment.name)}
                                                            </span>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {/* <div className="col-right" /> */}
            </div>

            <Modal
                className="modal-standard modal-form comments-modal"
                centered
                visible={ isModalOpen }
                width={ 430 }
                footer={ null }
                onCancel={ handleCancelModal }
                destroyOnClose={ false }
            >
                <strong className="modal-title">
                    { modalMode === "edit" ?
                        <Localize>FORMS.Title_EditComment</Localize> :
                        <Localize>FORMS.Title_AddComment</Localize>
                    }
                </strong>
                <Comments.Forms.Edit
                    comment={ activeComment }
                    onSubmit={ handleSubmitModal }
                    onCancel={ handleCancelModal }
                    onAddAttachment={ handleAddAttachment }
                    onRemoveAttachment={ handleRemoveAttachment }
                    loading={ loadingMutationStandCommentUpdate || loadingMutationStandCommentDelete || loadingLazyQueryStandComment }
                    loadingAttachmentCreate={ loadingMutationStandAttachmentCreate }
                    loadingAttachmentDelete={ loadingMutationStandAttachmentDelete }
                    errorText={ modalError }
                />
            </Modal>
        </div>
    );
};

export default StandCommentsSubPage;
